import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin, Typography } from 'antd'
import { filter, get, isEmpty, join, map, size, some } from 'lodash'

import { DirtyPrompt, UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  MetadataTreeErrors,
  ReduxMetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderCourseTypeEvent } from '../../helpers'
import {
  useCourseTypesQuery,
  useDeleteCourseTypeMutation,
  useReplaceCourseTypeMutation,
  useRevertReplaceCourseTypeMutation,
  useSaveCourseTypesMutation,
  useSetIsCurationEnabledCourseTypeMutation,
} from '../../hooks'
import {
  addCourseTypeNode,
  expandCourseTypeMainTreeKeys,
  expandCourseTypeSupportingTreeKeys,
  moveCourseTypes,
  renameCourseTypeNode,
  resetCourseTypeTree,
  setDataForRenamingCourseType,
} from '../../redux/actions'

const CourseTypesPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle(APP_ROUTES.COURSE_TYPES.name)
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)

  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.COURSE_TYPE], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useCourseTypesQuery()

  const [saveCourseTypes, { isLoading: isSaveLoading, error: saveError }] =
    useSaveCourseTypesMutation(refetchLastEvent)

  const [deleteCourseType] = useDeleteCourseTypeMutation(refetchLastEvent)

  const [setIsCurationEnabledCourseType] =
    useSetIsCurationEnabledCourseTypeMutation(refetchLastEvent)

  const [replaceCourseType] = useReplaceCourseTypeMutation(refetchLastEvent)

  const [revertReplaceCourseType] =
    useRevertReplaceCourseTypeMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(state => state.courseTypes.courseTypes)

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => dispatch(addCourseTypeNode(node)),
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id }) => deleteCourseType(id),
    [deleteCourseType],
  )

  const handleRevertReplaceCourseType = useCallback(
    ({ replaceToken }) => revertReplaceCourseType(replaceToken),
    [revertReplaceCourseType],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled }) =>
      setIsCurationEnabledCourseType(id, isCurationEnabled),
    [setIsCurationEnabledCourseType],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameCourseTypeNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingCourseType({ id })),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes
      const levelToMoveUnder = destinationNode.level - 1

      const parentSelectedNodes = filter(
        nodesToMove,
        ({ parentId }) => !some(nodesToMove, ({ id }) => id === parentId),
      )

      const restrictedToMoveNodes = filter(parentSelectedNodes, node => {
        if (levelToMoveUnder > node.level) {
          return !isEmpty(node.children)
        }

        if (levelToMoveUnder === node.level) {
          return !isEmpty(
            node.level === 1
              ? get(node, 'children[0].children')
              : node.children,
          )
        }

        return false
      })

      if (!isEmpty(restrictedToMoveNodes)) {
        return showErrorNotification({
          title: 'Move Error',
          description: (
            <>
              The following nodes can&apos;t be moved:
              {map(restrictedToMoveNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </>
          ),
        })
      }

      return dispatch(
        moveCourseTypes({
          nodesToMove: parentSelectedNodes,
          destinationNode,
        }),
      )
    },
    [dispatch],
  )

  const handleReplace = useCallback(
    async (nodesToReplace, replacementNodes) => {
      if (size(replacementNodes) > 1) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Can't Replace with more than 1 node",
        })
      }

      const [nodeToReplace] = nodesToReplace
      const [replacementNode] = replacementNodes

      if (nodeToReplace.isCurationEnabled) {
        return showErrorNotification({
          title: 'Replace Error',
          description:
            "The node can't be replaced as it's still enabled for curation",
        })
      }

      if (nodeToReplace.isIgnored && !replacementNode.isIgnored) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Ignored course type can't be replaced by non ignored",
        })
      }

      if (replacementNode.isIgnored && !nodeToReplace.isIgnored) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Non ignored course type can't be replaced by ignored",
        })
      }

      if (!isEmpty(replacementNode.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't replace as it's already being replaced by ${replacementNode.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's already being replaced by ${nodeToReplace.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replaces)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's replacing other nodes ${join(
            map(nodeToReplace.replaces, 'name'),
            ',',
          )}`,
        })
      }

      await replaceCourseType({
        idToReplace: nodeToReplace.id,
        replacementId: replacementNode.id,
      })

      return setIsReplaceActive(false)
    },
    [replaceCourseType],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetCourseTypeTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveCourseTypes(data),
    [data, saveCourseTypes],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Course Type error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  const { entities } = saveError || {}

  return isFetchLoading ||
    isSaveLoading ||
    isFetchLatestEventLoading ||
    isEmpty(treeData) ? (
    <Spin />
  ) : (
    <>
      <DirtyPrompt isDirty={isDirty} />
      <StyledPageHeader>
        <StyledPageTitle>Course Types</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderCourseTypeEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      {!isEmpty(entities) && <MetadataTreeErrors errors={entities} />}
      <ReduxMetadataTree
        disableLink={!hasPermissionToViewDetails}
        mainTreeExpandedKeys={mainTreeExpandedKeys}
        supportingTreeExpandedKeys={supportingTreeExpandedKeys}
        expandMainTree={expandCourseTypeMainTreeKeys}
        expandSupportingTree={expandCourseTypeSupportingTreeKeys}
        metadataType={METADATA_TYPES.COURSE_TYPES}
        isDirty={isDirty}
        showSearch
        allowReplaceMany={false}
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        moveNodes={handleMove}
        replaceNode={handleReplace}
        revertReplaceNode={handleRevertReplaceCourseType}
        setDataForRenaming={handleSetDataForRenaming}
        setIsCurationEnabled={handleSetIsCurationEnabled}
        dataForRenaming={dataForRenaming}
        isMoveActive={isMoveActive}
        setIsMoveActive={setIsMoveActive}
        isReplaceActive={isReplaceActive}
        setIsReplaceActive={setIsReplaceActive}
      />
    </>
  )
}

export default CourseTypesPage
